import { useState, useEffect, useMemo, useRef } from "react";
import { sqlRead, sqlEscape, clean } from "../utils/sqlClient";
import { fmtDate } from "../utils/format";
import { useStaleData } from "../hooks/useStaleData";

const BLOB_SAS = import.meta.env.VITE_QUOTE_DOCS_SAS || "";

// Which "Cost / X" unit(s) apply to a Category (optionally narrowed to a
// Subcategory), and the Category/Subcategory picklist itself, are admin-managed
// data now — see QuoteUnitMetrics / QuoteCategoryOptions (db/32, db/33), edited
// from the "Quote Log Setup" admin page (src/pages/QuoteLogSetup.jsx).
//
// A QuoteUnitMetrics.AttributeName may list several comma-separated aliases
// (e.g. "Unit CFM, Airflow") instead of one exact name — AI extraction on
// different vendors' quotes for the same category often lands on different
// wording for the same thing, and matching happens live at render time, so
// adding an alias here re-resolves every quote in that category retroactively,
// no data edit or migration needed.
function metricAliases(attributeName) {
  return (attributeName || "").split(",").map(s => s.trim()).filter(Boolean);
}

// For categories with no admin-configured unit mapping (e.g. "Other", or any category
// quoted as a services/lump-sum line rather than equipment), CFM/Tonnage/Quantity
// rarely apply. Instead of guessing a category-wide unit, scan the quote's own
// AI-extracted attributes for a recognizable countable quantity and report cost
// against whatever was actually captured (e.g. "Number of Rooms Analyzed" on a
// CFD/engineering-services quote → Cost / Room).
//
// A noun match alone isn't enough — "Door Height" and "Panel Thickness" both
// contain their noun but are dimensions, not counts, and dividing cost by a
// height/thickness value produces a nonsense per-unit number. COUNT_WORD_RE
// requires the attribute name to also look like a count (e.g. "Number of
// Doors", "Door Count", "Total Panels") before it's used as a denominator.
const COUNT_WORD_RE = /(?:number of|count of|total(?:\s+#)?|qty\.?|quantity)\b|\b(?:count|qty)\b\s*$/i;
const FALLBACK_UNIT_PATTERNS = [
  { label: "Cost / Room",    unit: "rooms",    re: /room/i,    needsCount: true },
  { label: "Cost / Door",    unit: "doors",    re: /\bdoor/i,  needsCount: true },
  { label: "Cost / Panel",   unit: "panels",   re: /\bpanel/i, needsCount: true },
  { label: "Cost / Fixture", unit: "fixtures", re: /fixture/i, needsCount: true },
  { label: "Cost / LF",      unit: "LF",       re: /linear\s*f(oo|ee)?t|(^|\W)lf(\W|$)/i },
  // Deliberately anchored to the WHOLE attribute name (not a substring test
  // like every pattern above) — this is the last-resort catch-all, so it must
  // NOT match a named sub-component's count (e.g. "Hinge Quantity" on a door
  // quote), only a genuine top-level unit count with no other noun attached
  // (e.g. "Quantity", "Qty", "Total Units", "Each"). A component quantity
  // would otherwise satisfy this unconditionally (no needsCount noun check
  // applies here), producing a technically-real but meaningless $/unit figure.
  { label: "Cost / Unit",    unit: "units",    re: /^(?:total\s+)?(?:quantity|qty\.?|units?|each)$/i },
];

function fmt$(n) {
  const v = +n;
  if (isNaN(v) || v === 0 || v === null || v === undefined) return "—";
  return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

// AI-extracted QuoteAttributes are free-text — only add thousands separators
// (and a $ prefix, dropping the "USD" suffix) when the value is a bare number,
// never touching a value that mixes in its own text (e.g. "2-8 C", "100% Lead/Lag").
function fmtAttrValue(value, unit) {
  const isNumeric = /^-?\d+(\.\d+)?$/.test((value ?? "").toString().trim());
  if (!isNumeric) return `${value ?? ""}${unit ? ` ${unit}` : ""}`;
  const num = +value;
  if (/^(usd|\$)$/i.test((unit || "").trim())) return fmt$(num);
  return `${num.toLocaleString()}${unit ? ` ${unit}` : ""}`;
}

function categoryColor(cat = "") {
  const c = cat.toLowerCase();
  if (c.includes("ahu") || c.includes("custom"))  return { bg: "#fef3c7", color: "#92400e" };
  if (c.includes("hepa"))                          return { bg: "#d1fae5", color: "#065f46" };
  if (c.includes("packaged") || c.includes("rtu")) return { bg: "#ede9fe", color: "#5b21b6" };
  if (c.includes("pump"))                          return { bg: "#fce7f3", color: "#9d174d" };
  if (c.includes("controls"))                      return { bg: "#e0f2fe", color: "#0369a1" };
  if (c.includes("panel") || c.includes("door"))  return { bg: "#f0fdf4", color: "#166534" };
  return { bg: "#f3f4f6", color: "#374151" };
}

// ── ComboBox ─────────────────────────────────────────────────────────────────
function ComboBox({ value, onChange, options, placeholder, style }) {
  const [inputVal, setInputVal] = useState(value || "");
  const [open, setOpen]         = useState(false);
  const containerRef            = useRef(null);

  useEffect(() => { setInputVal(value || ""); }, [value]);

  useEffect(() => {
    const handler = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        if (!options.includes(inputVal)) { setInputVal(""); onChange(""); }
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [inputVal, options, onChange]);

  const filtered = inputVal.trim()
    ? options.filter(o => o.toLowerCase().includes(inputVal.trim().toLowerCase()))
    : options;

  const select = opt => { setInputVal(opt); onChange(opt); setOpen(false); };

  return (
    <div ref={containerRef} style={{ position: "relative", ...style }}>
      <div style={{ position: "relative" }}>
        <input
          value={inputVal}
          onChange={e => { setInputVal(e.target.value); onChange(""); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          style={{
            height: 30, width: "100%", border: "1px solid var(--cs-border)",
            borderRadius: "var(--radius)", padding: "0 28px 0 9px",
            fontSize: 12, color: "var(--text)", background: "#fff",
            outline: "none", boxSizing: "border-box",
          }}
        />
        {inputVal ? (
          <button onClick={() => { setInputVal(""); onChange(""); setOpen(false); }} style={{
            position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
            background: "none", border: "none", cursor: "pointer",
            fontSize: 12, color: "var(--text3)", padding: "0 2px", lineHeight: 1,
          }}>✕</button>
        ) : (
          <span style={{
            position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
            fontSize: 10, color: "var(--text3)", pointerEvents: "none",
          }}>▾</span>
        )}
      </div>
      {open && filtered.length > 0 && (
        <div style={{
          position: "absolute", top: "calc(100% + 2px)", left: 0, right: 0,
          background: "#fff", border: "1px solid var(--cs-border)",
          borderRadius: "var(--radius)", boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          zIndex: 300, maxHeight: 220, overflowY: "auto",
        }}>
          {filtered.map((opt, i) => (
            <div key={i} onMouseDown={() => select(opt)} style={{
              padding: "7px 10px", fontSize: 12, cursor: "pointer",
              color: opt === value ? "var(--cs-blue)" : "var(--text)",
              background: opt === value ? "var(--cs-blue-lt)" : "transparent",
              fontWeight: opt === value ? 600 : 400,
            }}
              onMouseEnter={e => { if (opt !== value) e.currentTarget.style.background = "#f5f6f8"; }}
              onMouseLeave={e => { if (opt !== value) e.currentTarget.style.background = "transparent"; }}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, sub }) {
  return (
    <div style={{
      background: "var(--surface)", border: "1px solid var(--cs-border)",
      borderRadius: "var(--radius)", padding: "12px 14px",
    }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
        {label}
      </div>
      <div style={{
        fontSize: value && value !== "—" ? 18 : 14, fontWeight: 700,
        color: value && value !== "—" ? "var(--text)" : "var(--text3)",
        fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em",
      }}>
        {value || "—"}
      </div>
      {sub && <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ── Vendor Edit Flyout ────────────────────────────────────────────────────────
function VendorEditFlyout({ vendor, contacts, onClose, onSaved }) {
  const [form, setForm] = useState({
    vendor_name:   vendor.VendorName   || vendor.vendor_name   || "",
    vendor_id:     vendor.VendorID     || vendor.vendor_id     || "",
    website:       vendor.Website      || vendor.website       || "",
    notes:         vendor.Notes        || vendor.notes         || "",
  });
  const [contactList, setContactList] = useState(
    (contacts || []).map(c => ({
      contact_id:    c.ContactID    || c.contact_id    || null,
      contact_name:  c.ContactName  || c.contact_name  || "",
      contact_email: c.ContactEmail || c.contact_email || "",
      contact_phone: c.ContactPhone || c.contact_phone || "",
      contact_title: c.ContactTitle || c.contact_title || "",
    }))
  );
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [success, setSuccess] = useState(false);

  const updateContact = (i, key, val) =>
    setContactList(prev => prev.map((c, idx) => idx === i ? { ...c, [key]: val } : c));

  const addContact = () =>
    setContactList(prev => [...prev, { contact_id: null, contact_name: "", contact_email: "", contact_phone: "", contact_title: "" }]);

  const removeContact = i =>
    setContactList(prev => prev.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      const payload = JSON.stringify({
        vendor_id:   clean(form.vendor_id),
        vendor_name: clean(form.vendor_name),
        website:     clean(form.website),
        notes:       clean(form.notes),
        contacts:    contactList.map(c => ({
          contact_id:    c.contact_id,
          contact_name:  clean(c.contact_name),
          contact_email: clean(c.contact_email),
          contact_phone: clean(c.contact_phone),
          contact_title: clean(c.contact_title),
        })),
      });
      // sp_SaveVendor is granted to cs-est-sql-reader (db/40_vendors_admin.sql)
      // and called through sql-read's single-EXEC guard — the sql-write
      // endpoint is hardcoded to always run sp_WriteQuote regardless of body
      // content, so calling it here (as this used to) silently never actually
      // saved a vendor edit.
      // sqlEscape() on the whole JSON string is required even though every
      // field already goes through clean() above — clean() only strips
      // apostrophes from the *editable* fields; it doesn't cover contact_id or
      // any field added later, and without escaping here a stray raw ' breaks
      // out of the @payload='...' literal (sqlGuard then rejects the call as
      // malformed, surfacing as a generic "Database error" from sql-read).
      await sqlRead(`EXEC sp_SaveVendor @payload='${sqlEscape(payload)}'`);
      setSuccess(true);
      setTimeout(() => { onSaved(); onClose(); }, 800);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    height: 30, width: "100%", border: "1px solid var(--cs-border)",
    borderRadius: "var(--radius)", padding: "0 9px",
    fontSize: 12, color: "var(--text)", background: "#fff",
    outline: "none", boxSizing: "border-box",
  };
  const labelStyle = {
    fontSize: 10, fontWeight: 600, color: "var(--text3)",
    textTransform: "uppercase", letterSpacing: "0.06em",
    display: "block", marginBottom: 3,
  };

  return (
    <div style={{
      position: "fixed", top: 0, right: 0, width: 420, height: "100vh",
      background: "var(--surface)", borderLeft: "1px solid var(--cs-border)",
      display: "flex", flexDirection: "column",
      boxShadow: "-4px 0 24px rgba(0,0,0,0.07)", zIndex: 400,
    }}>
      {/* Header */}
      <div style={{
        padding: "14px 18px", borderBottom: "1px solid var(--cs-border)",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
      }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>Edit Vendor</div>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "var(--text3)", padding: "2px 6px" }}>✕</button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: "auto", padding: 18, display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Vendor info */}
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
            Vendor Info
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div>
              <label style={labelStyle}>Vendor Name</label>
              <input style={inputStyle} value={form.vendor_name}
                onChange={e => setForm(f => ({ ...f, vendor_name: e.target.value }))} />
            </div>
            <div>
              <label style={labelStyle}>Vendor ID</label>
              <input style={inputStyle} value={form.vendor_id}
                onChange={e => setForm(f => ({ ...f, vendor_id: e.target.value }))}
                placeholder="e.g. V001" />
            </div>
            <div>
              <label style={labelStyle}>Website</label>
              <input style={inputStyle} value={form.website}
                onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
                placeholder="https://…" />
            </div>
            <div>
              <label style={labelStyle}>Notes</label>
              <textarea value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={3}
                style={{ ...inputStyle, height: "auto", padding: "7px 9px", resize: "vertical" }} />
            </div>
          </div>
        </div>

        {/* Contacts */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Contacts
            </div>
            <button onClick={addContact} style={{
              fontSize: 11, fontWeight: 600, color: "var(--cs-blue)",
              background: "var(--cs-blue-lt)", border: "none", borderRadius: "var(--radius)",
              padding: "3px 10px", cursor: "pointer",
            }}>+ Add contact</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {contactList.length === 0 && (
              <div style={{ fontSize: 12, color: "var(--text3)", padding: "10px 0" }}>No contacts yet.</div>
            )}
            {contactList.map((c, i) => (
              <div key={i} style={{
                border: "1px solid var(--cs-border)", borderRadius: "var(--radius)",
                padding: "10px 12px", display: "flex", flexDirection: "column", gap: 7,
              }}>
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Name</label>
                    <input style={inputStyle} value={c.contact_name}
                      onChange={e => updateContact(i, "contact_name", e.target.value)} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Title</label>
                    <input style={inputStyle} value={c.contact_title}
                      onChange={e => updateContact(i, "contact_title", e.target.value)}
                      placeholder="e.g. Sales Rep" />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Email</label>
                  <input style={inputStyle} value={c.contact_email} type="email"
                    onChange={e => updateContact(i, "contact_email", e.target.value)} />
                </div>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Phone</label>
                    <input style={inputStyle} value={c.contact_phone} type="tel"
                      onChange={e => updateContact(i, "contact_phone", e.target.value)} />
                  </div>
                  <button onClick={() => removeContact(i)} style={{
                    height: 30, padding: "0 10px", fontSize: 11, fontWeight: 500,
                    border: "1px solid var(--cs-border)", borderRadius: "var(--radius)",
                    background: "none", color: "var(--red, #dc2626)", cursor: "pointer", flexShrink: 0,
                  }}>Remove</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        padding: "12px 18px", borderTop: "1px solid var(--cs-border)",
        display: "flex", flexDirection: "column", gap: 8, flexShrink: 0,
      }}>
        {error && (
          <div style={{ fontSize: 12, color: "var(--red, #dc2626)", padding: "6px 10px", background: "#fef2f2", borderRadius: "var(--radius)" }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ fontSize: 12, color: "#065f46", padding: "6px 10px", background: "#d1fae5", borderRadius: "var(--radius)" }}>
            Saved!
          </div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} style={{
            flex: 1, height: 34, fontSize: 13, fontWeight: 500,
            border: "1px solid var(--cs-border)", borderRadius: "var(--radius)",
            background: "none", color: "var(--text2)", cursor: "pointer",
          }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{
            flex: 2, height: 34, fontSize: 13, fontWeight: 600,
            border: "none", borderRadius: "var(--radius)",
            background: saving ? "var(--cs-border)" : "var(--cs-blue)",
            color: "#fff", cursor: saving ? "default" : "pointer",
          }}>{saving ? "Saving…" : "Save changes"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Gap report panel ────────────────────────────────────────────────────────────
// Flags quotes missing the inputs needed for cost benchmarking: no ScopeAreaSF
// (so $/SF can never be derived) and/or no QuoteLineItems rows (saved as a lump
// total with no line-item breakdown). Data comes from sp_GetQuoteGapReport
// (db/31_quote_gap_report.sql).
function GapReportPanel({ rows, loading, onSelectQuote }) {
  const [expanded, setExpanded] = useState(false);

  if (loading && rows.length === 0) {
    return (
      <div style={{ marginBottom: 16, fontSize: 12, color: "var(--text3)" }}>
        Checking quote intake gaps…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div style={{
        marginBottom: 16, border: "1px solid var(--cs-border)",
        borderRadius: "var(--radius)", padding: "10px 14px",
        fontSize: 12, color: "#065f46", background: "#d1fae5",
      }}>
        ✓ No quote intake gaps — every quote has a scope area and line items on file.
      </div>
    );
  }

  const missingSF    = rows.filter(r => !r.ScopeAreaSF);
  const missingLines = rows.filter(r => !r.LineItemCount);

  return (
    <div style={{
      marginBottom: 16, border: "1px solid #fde68a",
      borderRadius: "var(--radius)", overflow: "hidden", background: "#fffbeb",
    }}>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{
          padding: "10px 14px", display: "flex", alignItems: "center",
          justifyContent: "space-between", cursor: "pointer", flexWrap: "wrap", gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#92400e" }}>⚠ Quote Intake Quality Report</span>
          <span style={{ fontSize: 11, color: "#92400e" }}>
            {missingSF.length} missing $/SF · {missingLines.length} missing line items · {rows.length} flagged
          </span>
        </div>
        <span style={{ fontSize: 11, color: "#92400e" }}>{expanded ? "Hide ▲" : "Show ▼"}</span>
      </div>
      {expanded && (
        <div style={{ borderTop: "1px solid #fde68a", background: "#fff", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#FAFBFC" }}>
                <th style={{ padding: "7px 12px", fontWeight: 600, color: "var(--text3)", fontSize: 11, textAlign: "left", borderBottom: "1px solid var(--cs-border)" }}>Date</th>
                <th style={{ padding: "7px 12px", fontWeight: 600, color: "var(--text3)", fontSize: 11, textAlign: "left", borderBottom: "1px solid var(--cs-border)" }}>Vendor</th>
                <th style={{ padding: "7px 12px", fontWeight: 600, color: "var(--text3)", fontSize: 11, textAlign: "left", borderBottom: "1px solid var(--cs-border)" }}>Category</th>
                <th style={{ padding: "7px 12px", fontWeight: 600, color: "var(--text3)", fontSize: 11, textAlign: "right", borderBottom: "1px solid var(--cs-border)" }}>Total</th>
                <th style={{ padding: "7px 12px", fontWeight: 600, color: "var(--text3)", fontSize: 11, textAlign: "left", borderBottom: "1px solid var(--cs-border)" }}>Gaps</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.QuoteID}
                  onClick={() => onSelectQuote(r)}
                  style={{ borderBottom: i < rows.length - 1 ? "1px solid var(--cs-border)" : "none", cursor: "pointer" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#f8f9fb"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                >
                  <td style={{ padding: "8px 12px", color: "var(--text3)", whiteSpace: "nowrap" }}>{fmtDate(r.QuoteDate)}</td>
                  <td style={{ padding: "8px 12px", color: "var(--text)", fontWeight: 500, whiteSpace: "nowrap" }}>{r.VendorName || "—"}</td>
                  <td style={{ padding: "8px 12px", color: "var(--text2)", whiteSpace: "nowrap" }}>{r.Category || "—"}</td>
                  <td style={{ padding: "8px 12px", color: "var(--text)", fontWeight: 600, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{fmt$(r.TotalAmount)}</td>
                  <td style={{ padding: "8px 12px" }}>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {!r.ScopeAreaSF && (
                        <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 100, background: "#fef3c7", color: "#92400e", whiteSpace: "nowrap" }}>No $/SF</span>
                      )}
                      {!r.LineItemCount && (
                        <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 100, background: "#fee2e2", color: "#991b1b", whiteSpace: "nowrap" }}>No line items</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Category context panel ────────────────────────────────────────────────────
function CategoryContextPanel({ category, rows }) {
  // Group filtered rows by vendor and compute summary stats
  const inCat = rows.filter(r => (r.Category || "") === category);
  const byVendor = {};
  inCat.forEach(r => {
    const key = r.VendorName || "Unknown";
    if (!byVendor[key]) byVendor[key] = { count: 0, total: 0, latest: "" };
    byVendor[key].count++;
    byVendor[key].total += +r.TotalAmount || 0;
    if (!byVendor[key].latest || r.QuoteDate > byVendor[key].latest)
      byVendor[key].latest = r.QuoteDate;
  });
  const vendors = Object.entries(byVendor).sort((a, b) => b[1].total - a[1].total);
  const catColors = categoryColor(category);

  if (vendors.length === 0) return null;

  return (
    <div style={{
      marginBottom: 16, border: "1px solid var(--cs-border)",
      borderRadius: "var(--radius)", overflow: "hidden", background: "#fff",
    }}>
      <div style={{
        padding: "10px 14px", background: catColors.bg,
        borderBottom: "1px solid var(--cs-border)",
        display: "flex", alignItems: "center", gap: 8,
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: catColors.color }}>{category}</span>
        <span style={{ fontSize: 11, color: catColors.color, opacity: 0.7 }}>— {vendors.length} vendor{vendors.length !== 1 ? "s" : ""} · {inCat.length} quote{inCat.length !== 1 ? "s" : ""}</span>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ background: "#FAFBFC" }}>
            <th style={{ padding: "7px 14px", fontWeight: 600, color: "var(--text3)", fontSize: 11, textAlign: "left", borderBottom: "1px solid var(--cs-border)" }}>Vendor</th>
            <th style={{ padding: "7px 14px", fontWeight: 600, color: "var(--text3)", fontSize: 11, textAlign: "center", borderBottom: "1px solid var(--cs-border)" }}>Quotes</th>
            <th style={{ padding: "7px 14px", fontWeight: 600, color: "var(--text3)", fontSize: 11, textAlign: "right", borderBottom: "1px solid var(--cs-border)" }}>Total</th>
            <th style={{ padding: "7px 14px", fontWeight: 600, color: "var(--text3)", fontSize: 11, textAlign: "right", borderBottom: "1px solid var(--cs-border)" }}>Avg</th>
            <th style={{ padding: "7px 14px", fontWeight: 600, color: "var(--text3)", fontSize: 11, textAlign: "right", borderBottom: "1px solid var(--cs-border)" }}>Latest Quote</th>
          </tr>
        </thead>
        <tbody>
          {vendors.map(([vendorName, s], i) => (
            <tr key={vendorName} style={{ borderBottom: i < vendors.length - 1 ? "1px solid var(--cs-border)" : "none" }}>
              <td style={{ padding: "8px 14px", color: "var(--text)", fontWeight: 500 }}>{vendorName}</td>
              <td style={{ padding: "8px 14px", color: "var(--text2)", textAlign: "center" }}>{s.count}</td>
              <td style={{ padding: "8px 14px", color: "var(--text)", fontWeight: 600, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmt$(s.total)}</td>
              <td style={{ padding: "8px 14px", color: "var(--text2)", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmt$(s.total / s.count)}</td>
              <td style={{ padding: "8px 14px", color: "var(--text3)", textAlign: "right" }}>{fmtDate(s.latest)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Vendor context panel ──────────────────────────────────────────────────────
function VendorContextPanel({ vendorName, rows, onEdit }) {
  const [vendorDetail, setVendorDetail] = useState(null);
  const [contacts, setContacts]         = useState([]);
  const [loading, setLoading]           = useState(true);

  useEffect(() => {
    if (!vendorName) return;
    setLoading(true);
    Promise.all([
      sqlRead(`EXEC sp_GetVendorDetail @vendor_name='${vendorName.replace(/'/g, "")}'`).catch(() => []),
      sqlRead(`EXEC sp_GetVendorContacts @vendor_name='${vendorName.replace(/'/g, "")}'`).catch(() => []),
    ]).then(([vRows, cRows]) => {
      setVendorDetail(vRows?.[0] || null);
      setContacts(cRows || []);
    }).finally(() => setLoading(false));
  }, [vendorName]);

  const inVendor = rows.filter(r =>
    (r.VendorName || "") === vendorName || (r.ManufacturerName || "") === vendorName
  );
  const totalAmt = inVendor.reduce((s, r) => s + (+r.TotalAmount || 0), 0);
  const cats = [...new Set(inVendor.map(r => r.Category).filter(Boolean))];

  return (
    <div style={{
      marginBottom: 16, border: "1px solid var(--cs-border)",
      borderRadius: "var(--radius)", overflow: "hidden", background: "#fff",
    }}>
      <div style={{
        padding: "10px 14px", background: "#F8F9FB",
        borderBottom: "1px solid var(--cs-border)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div>
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{vendorName}</span>
          {vendorDetail?.VendorID && (
            <span style={{ fontSize: 11, color: "var(--text3)", marginLeft: 8 }}>{vendorDetail.VendorID}</span>
          )}
        </div>
        <button
          onClick={() => onEdit(vendorDetail || { VendorName: vendorName }, contacts)}
          style={{
            fontSize: 11, fontWeight: 600, padding: "4px 12px",
            border: "1px solid var(--cs-border)", borderRadius: "var(--radius)",
            background: "#fff", color: "var(--cs-blue)", cursor: "pointer",
          }}
        >
          Edit vendor →
        </button>
      </div>

      <div style={{ display: "flex", padding: "12px 14px", gap: 24, flexWrap: "wrap" }}>
        {/* Quote summary */}
        <div style={{ minWidth: 160 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Quote Summary</div>
          <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 3 }}>
            <strong style={{ color: "var(--text)" }}>{inVendor.length}</strong> quote{inVendor.length !== 1 ? "s" : ""}
          </div>
          <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 3 }}>
            Total: <strong style={{ color: "var(--text)", fontVariantNumeric: "tabular-nums" }}>{fmt$(totalAmt)}</strong>
          </div>
          {cats.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
              {cats.map(cat => {
                const cc = categoryColor(cat);
                return (
                  <span key={cat} style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 100, ...cc }}>
                    {cat}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Contacts */}
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
            Contacts
          </div>
          {loading && <div style={{ fontSize: 12, color: "var(--text3)" }}>Loading…</div>}
          {!loading && contacts.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--text3)" }}>
              No contacts on file.{" "}
              <button
                onClick={() => onEdit(vendorDetail || { VendorName: vendorName }, [])}
                style={{ fontSize: 12, color: "var(--cs-blue)", background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}
              >Add one →</button>
            </div>
          )}
          {!loading && contacts.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {contacts.map((c, i) => (
                <div key={i} style={{ fontSize: 12 }}>
                  <div style={{ fontWeight: 600, color: "var(--text)" }}>
                    {c.ContactName || c.contact_name || "—"}
                    {(c.ContactTitle || c.contact_title) && (
                      <span style={{ fontWeight: 400, color: "var(--text3)", marginLeft: 6 }}>
                        {c.ContactTitle || c.contact_title}
                      </span>
                    )}
                  </div>
                  {(c.ContactEmail || c.contact_email) && (
                    <a href={`mailto:${c.ContactEmail || c.contact_email}`}
                      style={{ color: "var(--cs-blue)", textDecoration: "none" }}>
                      {c.ContactEmail || c.contact_email}
                    </a>
                  )}
                  {(c.ContactPhone || c.contact_phone) && (
                    <div style={{ color: "var(--text2)" }}>{c.ContactPhone || c.contact_phone}</div>
                  )}
                </div>
              ))}
            </div>
          )}
          {!loading && vendorDetail?.Website && (
            <div style={{ marginTop: 8 }}>
              <a href={vendorDetail.Website} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 12, color: "var(--cs-blue)", textDecoration: "none" }}>
                {vendorDetail.Website}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Quote edit panel ────────────────────────────────────────────────────────────
// Ties into Quote Log Setup's unit-cost metrics: suggestedAttrs lists every
// AttributeName a QuoteUnitMetrics row for this quote's Category/Subcategory
// expects that isn't on this quote yet — a category-wide metric configured
// after this quote was saved just needs the estimator to add that one
// attribute here, no data migration required.
function QuoteEditPanel({
  form, setForm, categoryNames, subcategoryNames,
  editAttrs, suggestedAttrs, onAddSuggested, onAddBlank, onUpdateAttr, onRemoveAttr,
  saving, saveError, onSave, onCancel,
}) {
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const labelStyle = { fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 3 };
  const inputStyle = { height: 30, width: "100%", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 9px", fontSize: 12.5, color: "var(--text)", background: "#fff", outline: "none", boxSizing: "border-box" };

  return (
    <div style={{ border: "1px solid var(--cs-blue)", borderRadius: "var(--radius)", padding: 14, background: "var(--cs-blue-lt)" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--cs-blue)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
        Editing Quote
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
        <div>
          <label style={labelStyle}>Category</label>
          <select style={inputStyle} value={form.trade} onChange={e => { set("trade", e.target.value); set("subcategory", ""); }}>
            <option value="">— none —</option>
            {categoryNames.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Subcategory</label>
          <input style={inputStyle} list="qed-subcats" value={form.subcategory} onChange={e => set("subcategory", e.target.value)} placeholder="optional" />
          <datalist id="qed-subcats">
            {subcategoryNames.map(s => <option key={s} value={s} />)}
          </datalist>
        </div>
        <div>
          <label style={labelStyle}>Manufacturer</label>
          <input style={inputStyle} value={form.manufacturer_name} onChange={e => set("manufacturer_name", e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Quote Date</label>
          <input style={inputStyle} type="date" value={form.quote_date} onChange={e => set("quote_date", e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Total Amount ($)</label>
          <input style={inputStyle} type="number" value={form.total_amount} onChange={e => set("total_amount", e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Scope Area (SF)</label>
          <input style={inputStyle} type="number" value={form.scope_area_sf} onChange={e => set("scope_area_sf", e.target.value)} placeholder="e.g. 4200" />
        </div>
        <div>
          <label style={labelStyle}>Lead Time (weeks)</label>
          <input style={inputStyle} type="number" value={form.lead_time_weeks} onChange={e => set("lead_time_weeks", e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Valid Until</label>
          <input style={inputStyle} type="date" value={form.valid_until} onChange={e => set("valid_until", e.target.value)} />
        </div>
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Notes</label>
        <textarea style={{ ...inputStyle, height: "auto", padding: "7px 9px", resize: "vertical" }} rows={2}
          value={form.notes} onChange={e => set("notes", e.target.value)} />
      </div>

      <div style={{ borderTop: "1px solid rgba(24,95,165,0.15)", paddingTop: 10, marginBottom: 10 }}>
        <label style={labelStyle}>Attributes</label>
        {editAttrs.length === 0 && (
          <div style={{ fontSize: 12, color: "var(--text3)", padding: "4px 0" }}>No attributes yet.</div>
        )}
        {editAttrs.map((a, i) => (
          <div key={a.AttributeID ?? a._tempKey ?? i} style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center" }}>
            <input style={{ ...inputStyle, flex: "1 1 140px" }} placeholder="Name" value={a.AttributeName}
              onChange={e => onUpdateAttr(a, { AttributeName: e.target.value })} />
            <input style={{ ...inputStyle, flex: "1 1 140px" }} placeholder="Value" value={a.AttributeValue ?? ""}
              onChange={e => onUpdateAttr(a, { AttributeValue: e.target.value })} />
            <input style={{ ...inputStyle, flex: "0 1 80px" }} placeholder="Unit" value={a.AttributeUnit ?? ""}
              onChange={e => onUpdateAttr(a, { AttributeUnit: e.target.value })} />
            <button onClick={() => onRemoveAttr(a)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#d1d5db", flexShrink: 0 }}
              onMouseEnter={e => e.currentTarget.style.color = "#dc2626"} onMouseLeave={e => e.currentTarget.style.color = "#d1d5db"}>🗑</button>
          </div>
        ))}
        <button onClick={onAddBlank} style={{
          marginTop: 4, padding: "4px 10px", fontSize: 11, fontWeight: 700, background: "#fff", color: "var(--cs-blue)",
          border: "1px dashed var(--cs-blue)", borderRadius: 6, cursor: "pointer",
        }}>+ Add Attribute</button>

        {suggestedAttrs.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 10.5, color: "var(--text3)", marginBottom: 6 }}>
              Expected by this category's unit-cost metrics but not on this quote yet:
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {suggestedAttrs.map(name => (
                <button key={name} onClick={() => onAddSuggested(name)} style={{
                  fontSize: 11, padding: "3px 10px", borderRadius: 4, cursor: "pointer",
                  background: "#fff", color: "var(--cs-blue)", border: "1px solid rgba(24,95,165,0.25)",
                }}>+ {name}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      {saveError && (
        <div style={{ fontSize: 12, color: "var(--red, #dc2626)", padding: "6px 10px", background: "#fef2f2", borderRadius: "var(--radius)", marginBottom: 10 }}>
          {saveError}
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={onCancel} disabled={saving} style={{
          flex: 1, height: 32, fontSize: 12.5, fontWeight: 600, border: "1px solid var(--cs-border)",
          borderRadius: "var(--radius)", background: "#fff", color: "var(--text2)", cursor: saving ? "default" : "pointer",
        }}>Cancel</button>
        <button onClick={onSave} disabled={saving} style={{
          flex: 2, height: 32, fontSize: 12.5, fontWeight: 700, border: "none", borderRadius: "var(--radius)",
          background: saving ? "var(--cs-border)" : "var(--cs-blue)", color: "#fff", cursor: saving ? "default" : "pointer",
        }}>{saving ? "Saving…" : "Save Changes"}</button>
      </div>
    </div>
  );
}

// ── Quote drawer ──────────────────────────────────────────────────────────────
// The drawer proper only ever renders WITH a quote. QuoteDrawer (below) is the
// thin gate: a null quote renders nothing, so the body's hooks run in the same
// order on every render — an early `return null` placed before useMemos in one
// component makes React throw "Rendered more hooks than during the previous
// render" the first time a quote is opened (audit 2026-09-14). Keying the body
// by QuoteID also resets its edit state when a different quote is selected.
function QuoteDrawer(props) {
  return props.quote ? <QuoteDrawerBody key={props.quote.QuoteID} {...props} /> : null;
}

function QuoteDrawerBody({ quote, onClose, unitMetrics, catOptions, onSaved }) {
  const [attrs, setAttrs]         = useState(null);
  const [lineItems, setLineItems] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [drawerWidth, setDrawerWidth] = useState(520);

  // Edit Quote — local draft state, nothing hits the database until Save.
  const [editing, setEditing]           = useState(false);
  const [form, setForm]                 = useState(null);
  const [editAttrs, setEditAttrs]        = useState([]);
  const [attrDeletes, setAttrDeletes]    = useState([]); // AttributeIDs to delete on save
  const [quoteOverride, setQuoteOverride] = useState(null); // last-saved fields, so the drawer reflects a save immediately
  const [saving, setSaving]     = useState(false);
  const [saveError, setSaveError] = useState("");
  const tempAttrKeyRef = useRef(0);

  const onDragMouseDown = e => {
    e.preventDefault();
    const startX = e.clientX, startW = drawerWidth;
    const onMove = mv => setDrawerWidth(Math.min(900, Math.max(320, startW + (startX - mv.clientX))));
    const onUp   = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup",   onUp);
  };

  useEffect(() => {
    if (!quote) return;
    setLoading(true); setError(null); setAttrs(null); setLineItems(null);
    setEditing(false); setQuoteOverride(null); setSaveError("");
    Promise.all([
      sqlRead(`EXEC sp_GetQuoteAttributes @quote_id=${quote.QuoteID}`),
      sqlRead(`EXEC sp_GetQuoteLineItems @quote_id=${quote.QuoteID}`),
    ])
      .then(([a, li]) => { setAttrs(a); setLineItems(li); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [quote?.QuoteID]);

  const displayQuote = quoteOverride ? { ...quote, ...quoteOverride } : quote;

  function startEditing() {
    setForm({
      manufacturer_name: displayQuote.ManufacturerName || "",
      trade:              displayQuote.Category || "",
      subcategory:        displayQuote.Subcategory || "",
      quote_date:         (displayQuote.QuoteDate || "").slice(0, 10),
      total_amount:       displayQuote.TotalAmount ?? "",
      scope_area_sf:      displayQuote.ScopeAreaSF ?? "",
      lead_time_weeks:    displayQuote.LeadTimeWeeks ?? "",
      valid_until:        (displayQuote.ValidUntil || "").slice(0, 10),
      scope:              displayQuote.Scope || "",
      exclusions:         displayQuote.Exclusions || "",
      notes:              displayQuote.Notes || "",
    });
    setEditAttrs((attrs || []).map(a => ({ ...a })));
    setAttrDeletes([]);
    setSaveError("");
    setEditing(true);
  }

  function cancelEditing() {
    setEditing(false);
    setSaveError("");
  }

  function addSuggestedAttr(name) {
    if (editAttrs.some(a => (a.AttributeName || "").toLowerCase() === name.toLowerCase())) return;
    setEditAttrs(prev => [...prev, { AttributeID: null, _tempKey: (tempAttrKeyRef.current -= 1), AttributeName: name, AttributeValue: "", AttributeUnit: "" }]);
  }

  function addBlankAttr() {
    setEditAttrs(prev => [...prev, { AttributeID: null, _tempKey: (tempAttrKeyRef.current -= 1), AttributeName: "", AttributeValue: "", AttributeUnit: "" }]);
  }

  function updateEditAttr(row, patch) {
    setEditAttrs(prev => prev.map(a => a === row ? { ...a, ...patch } : a));
  }

  function removeEditAttr(row) {
    setEditAttrs(prev => prev.filter(a => a !== row));
    if (row.AttributeID) setAttrDeletes(prev => [...prev, row.AttributeID]);
  }

  async function handleSave() {
    setSaving(true);
    setSaveError("");
    try {
      const payload = { quote_id: quote.QuoteID, ...form };
      const result = await sqlRead(`EXEC sp_UpdateQuote @payload='${sqlEscape(JSON.stringify(payload))}'`);
      if (!result?.[0]?.Success) throw new Error(result?.[0]?.ErrorMessage || "Save failed");

      for (const id of attrDeletes) {
        await sqlRead(`EXEC sp_DeleteQuoteAttribute @payload='${sqlEscape(JSON.stringify({ attribute_id: id }))}'`);
      }
      for (const a of editAttrs) {
        if (!(a.AttributeName || "").trim()) continue;
        await sqlRead(`EXEC sp_SaveQuoteAttribute @payload='${sqlEscape(JSON.stringify({
          attribute_id: a.AttributeID ?? null, quote_id: quote.QuoteID,
          name: a.AttributeName, value: a.AttributeValue ?? "", unit: a.AttributeUnit || null,
        }))}'`);
      }

      const freshAttrs = await sqlRead(`EXEC sp_GetQuoteAttributes @quote_id=${quote.QuoteID}`);
      setAttrs(freshAttrs);
      setQuoteOverride({
        ManufacturerName: form.manufacturer_name || null,
        Category: form.trade || null,
        Subcategory: form.subcategory || null,
        QuoteDate: form.quote_date || null,
        TotalAmount: form.total_amount === "" ? null : +form.total_amount,
        ScopeAreaSF: form.scope_area_sf === "" ? null : +form.scope_area_sf,
        LeadTimeWeeks: form.lead_time_weeks === "" ? null : +form.lead_time_weeks,
        ValidUntil: form.valid_until || null,
        Scope: form.scope || null,
        Exclusions: form.exclusions || null,
        Notes: form.notes || null,
      });
      setEditing(false);
      onSaved?.();
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  }

  const attrMap = useMemo(() => {
    const map = {};
    (attrs || []).forEach(a => { map[(a.AttributeName || "").toLowerCase()] = a.AttributeValue; });
    return map;
  }, [attrs]);

  const total  = +displayQuote.TotalAmount || 0;
  const sf     = +displayQuote.ScopeAreaSF || null;
  const catColors = categoryColor(displayQuote.Category);

  // Admin-configured metrics for this exact Category+Subcategory take priority
  // over a Category-wide (Subcategory = NULL) entry — see db/32_quote_unit_metrics.sql.
  // While editing, react to the in-progress Category/Subcategory selection
  // (not just the last-saved one) so the suggested-attribute chips below stay
  // relevant if the estimator changes the category mid-edit.
  const activeCategory    = editing ? form?.trade : displayQuote.Category;
  const activeSubcategory = editing ? form?.subcategory : displayQuote.Subcategory;
  const configured = useMemo(() => {
    const bySubcat  = (unitMetrics || []).filter(m => m.Category === activeCategory && m.Subcategory === (activeSubcategory || null));
    const byCatOnly = (unitMetrics || []).filter(m => m.Category === activeCategory && !m.Subcategory);
    return bySubcat.length > 0 ? bySubcat : byCatOnly;
  }, [unitMetrics, activeCategory, activeSubcategory]);

  // Attribute names a category-wide metric expects but this quote doesn't have
  // yet — surfaced as one-click suggestions in edit mode (the tie-in between
  // Quote Log Setup's Unit Cost Metrics and this quote's actual data).
  const currentAttrNames = new Set((editing ? editAttrs : (attrs || [])).map(a => (a.AttributeName || "").toLowerCase()));
  const suggestedAttrs = configured
    .map(m => metricAliases(m.AttributeName))
    .filter(aliases => aliases.length && !aliases.some(a => currentAttrNames.has(a.toLowerCase())))
    .map(aliases => aliases[0]);

  const categoryNames = [...new Set((catOptions || []).filter(c => !c.Subcategory).map(c => c.Category))].sort();
  const subcategoryNames = [...new Set((catOptions || [])
    .filter(c => !!c.Subcategory && (!form?.trade || c.Category === form.trade))
    .map(c => c.Subcategory))].sort();

  const unitCards = useMemo(() => {
    if (configured.length > 0) {
      return configured.map(m => {
        // "Attribute Name" may list several comma-separated aliases (e.g.
        // "Unit CFM, Airflow") — the first one already present on this quote
        // wins, so a metric configured after the fact resolves against
        // whatever name AI extraction actually used, no data edit required.
        const aliases = metricAliases(m.AttributeName);
        const foundAlias = aliases.find(a => attrMap[a.toLowerCase()] !== undefined);
        const val = foundAlias ? +attrMap[foundAlias.toLowerCase()] || null : null;
        return {
          label: m.MetricLabel,
          value: val,
          sub: val ? `${val.toLocaleString()} ${m.UnitLabel || ""}`.trim() : `No ${aliases.join(" / ")} recorded`,
        };
      });
    }
    const seen = new Set();
    return FALLBACK_UNIT_PATTERNS
      .map(p => {
        const match = (attrs || []).find(a =>
          !seen.has(a.AttributeName) && p.re.test(a.AttributeName || "") &&
          (!p.needsCount || COUNT_WORD_RE.test(a.AttributeName || ""))
        );
        const val = match ? +match.AttributeValue || null : null;
        if (!match || !val) return null;
        seen.add(match.AttributeName);
        return { label: p.label, value: val, sub: `${val.toLocaleString()} ${match.AttributeUnit || p.unit} · ${match.AttributeName}` };
      })
      .filter(Boolean)
      .slice(0, 3);
  }, [configured, attrs, attrMap]);

  return (
    <div style={{
      position: "fixed", top: 0, right: 0, width: drawerWidth, height: "100vh",
      background: "var(--surface)", borderLeft: "1px solid var(--cs-border)",
      display: "flex", flexDirection: "column",
      boxShadow: "-4px 0 24px rgba(0,0,0,0.07)", zIndex: 200,
    }}>
      <div onMouseDown={onDragMouseDown} style={{
        position: "absolute", top: 0, left: -4, width: 8, height: "100%",
        cursor: "col-resize", zIndex: 201, display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <div style={{ width: 4, height: 48, borderRadius: 2, background: "var(--cs-border)", opacity: 0.8 }} />
      </div>

      <div style={{
        padding: "14px 18px", borderBottom: "1px solid var(--cs-border)",
        display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexShrink: 0,
      }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>{displayQuote.VendorName || "Quote"}</div>
          <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 2 }}>
            {[displayQuote.ManufacturerName, fmtDate(displayQuote.QuoteDate), `#${quote.QuoteID}`].filter(Boolean).join(" · ")}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
          {!editing && !loading && (
            <button onClick={startEditing} title="Edit quote" style={{
              background: "none", border: "1px solid var(--cs-border)", borderRadius: 6, cursor: "pointer",
              fontSize: 12, fontWeight: 600, color: "var(--cs-blue)", padding: "4px 10px",
            }}>✏️ Edit</button>
          )}
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "var(--text3)", padding: "2px 6px", lineHeight: 1 }}>✕</button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 18, display: "flex", flexDirection: "column", gap: 18 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {displayQuote.hubspot_deal_name && (
            <span style={{ fontSize: 12, fontWeight: 500, color: "var(--text2)", background: "var(--cs-blue-lt)", padding: "3px 10px", borderRadius: 4 }}>
              {displayQuote.hubspot_deal_name}
            </span>
          )}
          {displayQuote.Category && (
            <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 100, ...catColors }}>{displayQuote.Category}</span>
          )}
          {displayQuote.Subcategory && (
            <span style={{ fontSize: 11, fontWeight: 500, padding: "3px 10px", borderRadius: 100, background: "var(--surface)", border: "1px solid var(--cs-border)", color: "var(--text2)" }}>
              {displayQuote.Subcategory}
            </span>
          )}
        </div>

        {editing && (
          <QuoteEditPanel
            form={form} setForm={setForm}
            categoryNames={categoryNames} subcategoryNames={subcategoryNames}
            editAttrs={editAttrs} suggestedAttrs={suggestedAttrs}
            onAddSuggested={addSuggestedAttr} onAddBlank={addBlankAttr}
            onUpdateAttr={updateEditAttr} onRemoveAttr={removeEditAttr}
            saving={saving} saveError={saveError}
            onSave={handleSave} onCancel={cancelEditing}
          />
        )}

        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Financial Summary</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <MetricCard label="Total Quote" value={fmt$(total)} />
            {unitCards.map((c, i) => (
              <MetricCard key={i} label={c.label} value={c.value ? fmt$(total / c.value) : "—"} sub={c.sub} />
            ))}
            <MetricCard label="Cost / SF" value={sf ? fmt$(total / sf) : "—"} sub={sf ? `${sf.toLocaleString()} SF scope` : "No scope SF recorded"} />
          </div>
        </div>

        {loading && (
          <div style={{ textAlign: "center", padding: 24, color: "var(--text3)", fontSize: 13 }}>
            <div className="spinner spinner-dark" style={{ display: "inline-block", marginRight: 8 }} />Loading detail…
          </div>
        )}
        {error && (
          <div style={{ fontSize: 13, color: "var(--red)", padding: "10px 14px", background: "var(--red-lt)", borderRadius: "var(--radius)" }}>{error}</div>
        )}
        {quote.SourceFileUrl && (
          <a href={`${quote.SourceFileUrl}?${BLOB_SAS}`} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 13, color: "var(--cs-blue)", textDecoration: "none", fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 6 }}>
            📄 View original quote →
          </a>
        )}

        {attrs !== null && !editing && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Quote Attributes</div>
            <div style={{ border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", overflow: "hidden" }}>
              {attrs.length === 0 ? (
                <div style={{ padding: "12px 14px", fontSize: 12, color: "var(--text3)" }}>No attributes recorded</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <tbody>
                    {attrs.map((a, i) => (
                      <tr key={i} style={{ borderBottom: i < attrs.length - 1 ? "1px solid var(--cs-border)" : "none" }}>
                        <td style={{ padding: "7px 14px", color: "var(--text3)", width: "55%" }}>{a.AttributeName}</td>
                        <td style={{ padding: "7px 14px", color: "var(--text)", fontWeight: 500, textAlign: "right" }}>
                          {fmtAttrValue(a.AttributeValue, a.AttributeUnit)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {lineItems !== null && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
              Line Items {lineItems.length > 0 && <span style={{ fontWeight: 400, textTransform: "none", fontSize: 11 }}>({lineItems.length})</span>}
            </div>
            <div style={{ border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", overflow: "hidden" }}>
              {lineItems.length === 0 ? (
                <div style={{ padding: "12px 14px", fontSize: 12, color: "var(--text3)" }}>No line items recorded</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#FAFBFC", borderBottom: "1px solid var(--cs-border)" }}>
                      <th style={{ padding: "6px 12px", fontWeight: 600, color: "var(--text3)", fontSize: 11, textAlign: "left" }}>Description</th>
                      <th style={{ padding: "6px 8px",  fontWeight: 600, color: "var(--text3)", fontSize: 11, textAlign: "center" }}>Qty</th>
                      <th style={{ padding: "6px 8px",  fontWeight: 600, color: "var(--text3)", fontSize: 11, textAlign: "center" }}>Unit</th>
                      <th style={{ padding: "6px 12px", fontWeight: 600, color: "var(--text3)", fontSize: 11, textAlign: "right" }}>Unit $</th>
                      <th style={{ padding: "6px 12px", fontWeight: 600, color: "var(--text3)", fontSize: 11, textAlign: "right" }}>Extended</th>
                      <th style={{ padding: "6px 12px", fontWeight: 600, color: "var(--text3)", fontSize: 11, textAlign: "right" }}>$ / SF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((li, i) => (
                      <tr key={i} style={{ borderBottom: i < lineItems.length - 1 ? "1px solid var(--cs-border)" : "none" }}>
                        <td style={{ padding: "7px 12px", color: "var(--text)", wordBreak: "break-word" }}>{li.Description || li.ItemDescription || "—"}</td>
                        <td style={{ padding: "7px 8px",  color: "var(--text2)", textAlign: "center" }}>{li.qty ?? li.Quantity ?? "—"}</td>
                        <td style={{ padding: "7px 8px",  color: "var(--text3)", textAlign: "center", fontSize: 11 }}>{li.Unit || "—"}</td>
                        <td style={{ padding: "7px 12px", color: "var(--text)",  textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                          {(li.unit_price ?? li.UnitPrice) ? fmt$(li.unit_price ?? li.UnitPrice) : "—"}
                        </td>
                        <td style={{ padding: "7px 12px", color: "var(--text)",  textAlign: "right", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                          {(li.extended_price ?? li.ExtendedPrice) ? fmt$(li.extended_price ?? li.ExtendedPrice) : "—"}
                        </td>
                        <td style={{ padding: "7px 12px", color: sf ? "var(--text2)" : "var(--text3)", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                          {(li.extended_price ?? li.ExtendedPrice) && sf ? fmt$((li.extended_price ?? li.ExtendedPrice) / sf) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main QuoteSearch panel ────────────────────────────────────────────────────
export default function QuoteSearch() {
  const [fCategory,     setFCategory]     = useState("");
  const [fSubcategory,  setFSubcategory]  = useState("");
  const [fVendor,       setFVendor]       = useState("");
  const [fManufacturer, setFManufacturer] = useState("");
  const [fDeal,         setFDeal]         = useState("");
  const [fDateFrom,     setFDateFrom]     = useState("");
  const [fDateTo,       setFDateTo]       = useState("");
  const [fMinAmt,       setFMinAmt]       = useState("");
  const [fMaxAmt,       setFMaxAmt]       = useState("");
  const [fSearch,       setFSearch]       = useState("");

  const { data: cachedRows, loading, refreshing, refresh: refreshQuotes } = useStaleData(
    "cache:quotes",
    () => sqlRead("EXEC sp_SearchQuotes").then(rows => rows.slice(0, 50)),
  );
  const allRows = cachedRows ?? [];
  const searched = cachedRows !== null;
  const { data: cachedGapRows, loading: gapLoading } = useStaleData(
    "cache:quoteGaps",
    () => sqlRead("EXEC sp_GetQuoteGapReport").catch(() => []),
  );
  const gapRows = cachedGapRows ?? [];

  const { data: cachedUnitMetrics } = useStaleData(
    "cache:quoteUnitMetrics",
    () => sqlRead("EXEC sp_GetQuoteUnitMetrics").catch(() => []),
  );
  const unitMetrics = cachedUnitMetrics ?? [];

  const { data: cachedCatOptions } = useStaleData(
    "cache:quoteCategoryOptions",
    () => sqlRead("EXEC sp_GetQuoteCategoryOptions").catch(() => []),
  );
  const catOptions = cachedCatOptions ?? [];

  const [sortCol, setSortCol] = useState("QuoteDate");
  const [sortAsc, setSortAsc] = useState(false);

  const [selectedQuote, setSelectedQuote] = useState(null);

  // Vendor edit flyout
  const [editVendor,   setEditVendor]   = useState(null);
  const [editContacts, setEditContacts] = useState([]);

  const vendorOptions       = [...new Set(allRows.map(r => r.VendorName).filter(Boolean))].sort();
  // Category/Subcategory options are admin-managed (Quote Log Setup — QuoteCategoryOptions,
  // db/33_quote_categories.sql), unioned with anything already seen on loaded quotes so a
  // legacy value never disappears from the filter just because it hasn't been added there yet.
  // Subcategory options narrow to the selected Category, once one is picked.
  const categoryOptions     = [...new Set([...catOptions.map(o => o.Category), ...allRows.map(r => r.Category).filter(Boolean)])].sort();
  const subcategoryOptions  = [...new Set([
    ...catOptions.filter(o => o.Subcategory && (!fCategory || o.Category === fCategory)).map(o => o.Subcategory),
    ...allRows.filter(r => !fCategory || r.Category === fCategory).map(r => r.Subcategory).filter(Boolean),
  ])].sort();
  const manufacturerOptions = [...new Set(allRows.map(r => r.ManufacturerName).filter(Boolean))].sort();
  const dealOptions         = [...new Set(allRows.map(r => r.hubspot_deal_name).filter(Boolean))].sort();

  const clearFilters = () => {
    setFCategory(""); setFSubcategory(""); setFVendor(""); setFManufacturer("");
    setFDeal(""); setFDateFrom(""); setFDateTo(""); setFMinAmt(""); setFMaxAmt("");
    setFSearch("");
  };

  const results = allRows.filter(r => {
    if (fCategory        && (r.Category          || "") !== fCategory)                                           return false;
    if (fSubcategory     && (r.Subcategory        || "") !== fSubcategory)                                       return false;
    if (fVendor.trim()   && !(r.VendorName        || "").toLowerCase().includes(fVendor.trim().toLowerCase()))   return false;
    if (fManufacturer    && (r.ManufacturerName   || "") !== fManufacturer)                                      return false;
    if (fDeal.trim()     && !(r.hubspot_deal_name || "").toLowerCase().includes(fDeal.trim().toLowerCase()))     return false;
    if (fDateFrom        && r.QuoteDate && r.QuoteDate < fDateFrom)                                              return false;
    if (fDateTo          && r.QuoteDate && r.QuoteDate > fDateTo)                                                return false;
    if (fMinAmt          && (+r.TotalAmount || 0) < +fMinAmt)                                                    return false;
    if (fMaxAmt          && (+r.TotalAmount || 0) > +fMaxAmt)                                                    return false;
    if (fSearch.trim()) {
      const term = fSearch.trim().toLowerCase();
      const haystack = [
        r.VendorName, r.ManufacturerName, r.Category, r.Subcategory,
        r.hubspot_deal_name, r.Scope, r.Notes,
      ].map(v => (v || "").toLowerCase()).join(" ");
      if (!haystack.includes(term)) return false;
    }
    return true;
  });

  const sorted = [...results].sort((a, b) => {
    let av = a[sortCol], bv = b[sortCol];
    if (sortCol === "TotalAmount")  { av = +av || 0; bv = +bv || 0; }
    else if (sortCol === "QuoteDate") { av = new Date(av); bv = new Date(bv); }
    else { av = (av || "").toString().toLowerCase(); bv = (bv || "").toString().toLowerCase(); }
    return sortAsc ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  });

  const toggleSort = col => {
    if (sortCol === col) setSortAsc(a => !a);
    else { setSortCol(col); setSortAsc(col !== "QuoteDate" && col !== "TotalAmount"); }
  };
  const sortIcon = col => col === sortCol ? (sortAsc ? " ↑" : " ↓") : "";

  const totalAmt = results.reduce((s, r) => s + (+r.TotalAmount || 0), 0);
  const avgAmt   = results.length ? totalAmt / results.length : 0;

  const showCategoryPanel  = !!fCategory && !fVendor && !fManufacturer;
  const showVendorPanel    = !!(fVendor || fManufacturer) && !fCategory;
  const contextVendorName  = fVendor || fManufacturer;

  const filterInputStyle = {
    height: 30, border: "1px solid var(--cs-border)",
    borderRadius: "var(--radius)", padding: "0 9px",
    fontSize: 12, color: "var(--text)", background: "#fff",
    outline: "none", width: "100%",
  };
  const filterLabelStyle = {
    fontSize: 10, fontWeight: 600, color: "var(--text3)",
    textTransform: "uppercase", letterSpacing: "0.06em",
    display: "block", marginBottom: 3,
  };
  const thStyle = col => ({
    padding: "8px 12px", fontSize: 11, fontWeight: 600,
    textTransform: "uppercase", letterSpacing: "0.06em",
    textAlign: col === "TotalAmount" ? "right" : "left",
    whiteSpace: "nowrap", cursor: "pointer", userSelect: "none",
    borderBottom: "1px solid var(--cs-border)",
    color: sortCol === col ? "var(--cs-blue)" : "var(--text3)",
  });

  const hasEditFlyout = !!editVendor;
  const hasQuoteDrawer = !!selectedQuote;

  return (
    <>
      {/* Quote drawer */}
      {hasQuoteDrawer && (
        <QuoteDrawer quote={selectedQuote} onClose={() => setSelectedQuote(null)} unitMetrics={unitMetrics} catOptions={catOptions} onSaved={refreshQuotes} />
      )}

      {/* Vendor edit flyout — layered on top of quote drawer */}
      {hasEditFlyout && (
        <VendorEditFlyout
          vendor={editVendor}
          contacts={editContacts}
          onClose={() => setEditVendor(null)}
          onSaved={() => { refreshQuotes(); }}
        />
      )}

      <div className="panel-content" style={{
        maxWidth: "none",
        paddingRight: (hasQuoteDrawer || hasEditFlyout) ? 540 : 0,
        transition: "padding-right 0.2s ease",
      }}>
        <div className="panel-title">Quote Search</div>
        <div className="panel-sub">Search and filter all vendor quotes in the database. Click any row to see full detail, attributes, line items, and unit cost metrics.</div>

        <GapReportPanel rows={gapRows} loading={gapLoading} onSelectQuote={setSelectedQuote} />

        {/* Search bar */}
        <div style={{ marginBottom: 10, display: "flex", gap: 8 }}>
          <input
            type="text"
            value={fSearch}
            onChange={e => setFSearch(e.target.value)}
            onKeyDown={e => e.key === "Escape" && setFSearch("")}
            placeholder="Search quotes — vendor, manufacturer, category, deal, scope…"
            style={{
              flex: 1, height: 36, border: "1px solid var(--cs-border)",
              borderRadius: "var(--radius)", padding: "0 12px",
              fontSize: 14, color: "var(--text)", background: "#fff", outline: "none",
            }}
          />
          {fSearch && (
            <button
              onClick={() => setFSearch("")}
              style={{
                height: 36, padding: "0 14px", fontSize: 13, fontWeight: 500,
                border: "1px solid var(--cs-border)", borderRadius: "var(--radius)",
                background: "none", color: "var(--text2)", cursor: "pointer",
              }}
            >Clear</button>
          )}
        </div>

        {/* Filter bar — Category, Subcategory, Vendor, Manufacturer, then rest */}
        <div style={{
          background: "var(--surface)", border: "1px solid var(--cs-border)",
          borderRadius: "var(--radius)", padding: "14px 16px",
          marginBottom: 16, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end",
        }}>
          <div style={{ flex: "1 1 160px", minWidth: 150 }}>
            <label style={filterLabelStyle}>Category</label>
            <ComboBox value={fCategory} onChange={setFCategory} options={categoryOptions} placeholder="All categories…" />
          </div>
          <div style={{ flex: "1 1 140px", minWidth: 130 }}>
            <label style={filterLabelStyle}>Subcategory</label>
            <ComboBox value={fSubcategory} onChange={setFSubcategory} options={subcategoryOptions} placeholder="All subcategories…" />
          </div>
          <div style={{ flex: "1 1 140px", minWidth: 130 }}>
            <label style={filterLabelStyle}>Vendor</label>
            <ComboBox value={fVendor} onChange={setFVendor} options={vendorOptions} placeholder="Search vendor…" />
          </div>
          <div style={{ flex: "1 1 140px", minWidth: 130 }}>
            <label style={filterLabelStyle}>Manufacturer</label>
            <ComboBox value={fManufacturer} onChange={setFManufacturer} options={manufacturerOptions} placeholder="Search manufacturer…" />
          </div>
          <div style={{ flex: "1 1 140px", minWidth: 130 }}>
            <label style={filterLabelStyle}>Deal / Project</label>
            <ComboBox value={fDeal} onChange={setFDeal} options={dealOptions} placeholder="Search deal…" />
          </div>
          <div style={{ flex: "0 1 120px", minWidth: 110 }}>
            <label style={filterLabelStyle}>Date from</label>
            <input type="date" style={filterInputStyle} value={fDateFrom} onChange={e => setFDateFrom(e.target.value)} />
          </div>
          <div style={{ flex: "0 1 120px", minWidth: 110 }}>
            <label style={filterLabelStyle}>Date to</label>
            <input type="date" style={filterInputStyle} value={fDateTo} onChange={e => setFDateTo(e.target.value)} />
          </div>
          <div style={{ flex: "0 1 110px", minWidth: 100 }}>
            <label style={filterLabelStyle}>Min ($)</label>
            <input type="number" style={filterInputStyle} value={fMinAmt} placeholder="0" onChange={e => setFMinAmt(e.target.value)} />
          </div>
          <div style={{ flex: "0 1 110px", minWidth: 100 }}>
            <label style={filterLabelStyle}>Max ($)</label>
            <input type="number" style={filterInputStyle} value={fMaxAmt} placeholder="Any" onChange={e => setFMaxAmt(e.target.value)} />
          </div>
          <div style={{ display: "flex", gap: 6, alignSelf: "flex-end" }}>
            <button onClick={clearFilters} style={{
              height: 30, padding: "0 12px", fontSize: 12, fontWeight: 500,
              border: "1px solid var(--cs-border)", borderRadius: "var(--radius)",
              background: "none", color: "var(--text2)", cursor: "pointer",
            }}>Clear</button>
          </div>
        </div>

        {/* Context panel — appears between filters and results */}
        {showCategoryPanel && (
          <CategoryContextPanel category={fCategory} rows={allRows} />
        )}
        {showVendorPanel && (
          <VendorContextPanel
            vendorName={contextVendorName}
            rows={allRows}
            onEdit={(vendor, contacts) => { setEditVendor(vendor); setEditContacts(contacts); }}
          />
        )}

        {/* Results meta */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: "var(--text3)" }}>
            {loading ? "Loading…" : searched ? `${results.length} quote${results.length !== 1 ? "s" : ""}` : "—"}
            {refreshing && <span style={{ marginLeft: 8, opacity: 0.5 }}>↻</span>}
          </div>
          {results.length > 0 && !loading && (
            <div style={{ display: "flex", gap: 18 }}>
              <span style={{ fontSize: 12, color: "var(--text2)" }}>
                Total: <strong style={{ color: "var(--text)" }}>{fmt$(totalAmt)}</strong>
              </span>
              <span style={{ fontSize: 12, color: "var(--text2)" }}>
                Avg: <strong style={{ color: "var(--text)" }}>{fmt$(avgAmt)}</strong>
              </span>
            </div>
          )}
        </div>

        {/* Table */}
        <div style={{ border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", overflow: "hidden", background: "#fff" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ background: "#FAFBFC" }}>
              <tr>
                <th style={thStyle("QuoteDate")}         onClick={() => toggleSort("QuoteDate")}>Date{sortIcon("QuoteDate")}</th>
                <th style={thStyle("hubspot_deal_name")} onClick={() => toggleSort("hubspot_deal_name")}>Deal{sortIcon("hubspot_deal_name")}</th>
                <th style={thStyle("VendorName")}        onClick={() => toggleSort("VendorName")}>Vendor{sortIcon("VendorName")}</th>
                <th style={thStyle("ManufacturerName")}  onClick={() => toggleSort("ManufacturerName")}>Manufacturer{sortIcon("ManufacturerName")}</th>
                <th style={thStyle("Category")}          onClick={() => toggleSort("Category")}>Category{sortIcon("Category")}</th>
                <th style={thStyle("Subcategory")}       onClick={() => toggleSort("Subcategory")}>Subcategory{sortIcon("Subcategory")}</th>
                <th style={{ ...thStyle("TotalAmount"),  textAlign: "right" }} onClick={() => toggleSort("TotalAmount")}>Total{sortIcon("TotalAmount")}</th>
                <th style={{ ...thStyle("ScopeAreaSF"),  textAlign: "right" }} onClick={() => toggleSort("ScopeAreaSF")}>$ / SF{sortIcon("ScopeAreaSF")}</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>
                  <div className="spinner spinner-dark" style={{ display: "inline-block", marginRight: 8 }} />Searching…
                </td></tr>
              )}
              {!loading && searched && sorted.length === 0 && (
                <tr><td colSpan={8} style={{ padding: "48px 24px", textAlign: "center", color: "var(--text3)", fontSize: 13 }}>
                  <div style={{ fontSize: 28, marginBottom: 10 }}>🔍</div>
                  No quotes match your filters
                </td></tr>
              )}
              {!loading && sorted.map((r, i) => {
                const isSelected = selectedQuote?.QuoteID === r.QuoteID;
                const catColors  = categoryColor(r.Category);
                return (
                  <tr key={r.QuoteID}
                    onClick={() => setSelectedQuote(isSelected ? null : r)}
                    style={{
                      borderBottom: "1px solid var(--cs-border)", cursor: "pointer",
                      background: isSelected ? "var(--cs-blue-lt)" : "transparent",
                      transition: "background 0.1s",
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "#f8f9fb"; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                  >
                    <td style={{ padding: "9px 12px", fontSize: 12, color: "var(--text3)", whiteSpace: "nowrap" }}>{fmtDate(r.QuoteDate)}</td>
                    <td style={{ padding: "9px 12px", fontSize: 13, color: "var(--text)", fontWeight: 500, maxWidth: 160, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.hubspot_deal_name || "—"}
                    </td>
                    <td style={{ padding: "9px 12px", fontSize: 13, color: "var(--text2)", whiteSpace: "nowrap" }}>{r.VendorName || "—"}</td>
                    <td style={{ padding: "9px 12px", fontSize: 12, color: "var(--text3)", whiteSpace: "nowrap" }}>{r.ManufacturerName || "—"}</td>
                    <td style={{ padding: "9px 12px" }}>
                      {r.Category ? (
                        <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 100, ...catColors }}>{r.Category}</span>
                      ) : "—"}
                    </td>
                    <td style={{ padding: "9px 12px", fontSize: 12, color: "var(--text2)", whiteSpace: "nowrap" }}>{r.Subcategory || "—"}</td>
                    <td style={{ padding: "9px 12px", fontSize: 13, fontWeight: 600, color: "var(--text)", textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                      {fmt$(r.TotalAmount)}
                    </td>
                    <td style={{ padding: "9px 12px", fontSize: 12, color: r.ScopeAreaSF ? "var(--text2)" : "var(--text3)", textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                      {r.ScopeAreaSF && r.TotalAmount ? fmt$(r.TotalAmount / r.ScopeAreaSF) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
